import React, { useState, useMemo } from 'react';
import {
  Card,
  Flex,
  Typography,
  Radio,
  Select,
  Tag,
  Divider,
} from 'antd';

const formatNumber = (value) => {
  if (isNaN(value) || value === null || value === undefined) {
    return '0';
  }
  return Math.round(value).toLocaleString();
};

const calculateGeorgianCustoms = (params) => {
  const {
    vehicleType = 'car',
    engineVolume = 0,
    engineVolumeUnit = 'L',
    vehicleAge = 0,
    isElectric = false,
    isHybrid = false,
    registrationType = 'single',
    wheelPosition = 'left',
  } = params;

  let engineVolumeInLiters = engineVolumeUnit === 'CC' ? engineVolume / 1000 : engineVolume;

  const CUSTOMS_SERVICE_FEE = 150;
  const EXPERT_ASSESSMENT = 30;
  const CUSTOMS_DECLARATION = 50;
  const INTERNAL_TRANSIT = 50;

  const REGISTRATION_FEE =
    vehicleType === 'car' ? (registrationType === 'double' ? 380 : 200) : 145;

  const getCarExciseRate = (age) => {
    if (age < 3) return 1.5;
    if (age === 3) return 1.4;
    if (age === 4) return 1.2;
    if (age === 5) return 1.0;
    if (age > 5 && age < 9) return 0.8;
    if (age === 9) return 0.9;
    if (age === 10) return 1.1;
    if (age === 11) return 1.3;
    if (age === 12) return 1.5;
    if (age === 13) return 1.8;
    if (age === 14) return 2.1;
    if (age > 14) return 2.4;
    return 1.5;
  };

  const getMotorcycleExciseRate = (age) => {
    if (age < 1) return 1.5;
    if (age === 1) return 1.3;
    if (age >= 2 && age <= 5) return 0.7;
    if (age >= 6 && age <= 9) return 0.8;
    if (age === 10) return 1.0;
    if (age === 11) return 1.3;
    if (age === 12) return 1.5;
    if (age === 13) return 1.8;
    if (age === 14) return 2.1;
    if (age > 14) return 2.4;
    return 1.5;
  };

  if (isElectric && wheelPosition === 'right' && vehicleType === 'car') {
    engineVolumeInLiters = 2.0;
  }

  const exciseRate =
    isElectric
      ? 0
      : vehicleType === 'motorcycle'
        ? getMotorcycleExciseRate(vehicleAge)
        : getCarExciseRate(vehicleAge);

  let exciseTax = 0;
  if (vehicleType === 'motorcycle') {
    exciseTax = exciseRate * engineVolume;
  } else {
    exciseTax = Math.round(exciseRate * engineVolumeInLiters * 1000);
    if (wheelPosition === 'right' && !isElectric) {
      exciseTax *= 3;
    }
    if (isHybrid && vehicleAge <= 7 && wheelPosition === 'left') {
      exciseTax = Math.round((40 * exciseTax) / 100);
    }
    if (isElectric) {
      exciseTax = 0;
    }
  }

  let importTax = 0;
  if (vehicleType === 'motorcycle') {
    importTax = 0;
  } else if (isElectric && wheelPosition === 'left') {
    importTax = 0;
  } else {
    const engineVolumeInCC = engineVolumeInLiters * 1000;
    importTax = Math.round(0.05 * engineVolumeInCC + engineVolumeInCC * vehicleAge * 0.0025);
  }

  const breakdown = {
    exciseTax: Math.round(exciseTax) || 0,
    customsServiceFee: CUSTOMS_SERVICE_FEE,
    registrationFee: REGISTRATION_FEE,
    importTax: Math.round(importTax) || 0,
    expertAssessment: EXPERT_ASSESSMENT,
    customsDeclaration: CUSTOMS_DECLARATION,
    internalTransit: INTERNAL_TRANSIT,
  };

  const total = Object.values(breakdown).reduce((sum, value) => sum + value, 0);

  return {
    total,
    breakdown,
    parameters: {
      vehicleType,
      engineVolume,
      engineVolumeUnit,
      engineVolumeInLiters,
      vehicleAge,
      isElectric,
      isHybrid,
      registrationType,
      wheelPosition,
      exciseRate: isElectric ? 0 : exciseRate,
    },
  };
};

const generateCarEngineVolumes = () => {
  const volumes = [];
  for (let i = 0.1; i <= 13.0; i += 0.1) {
    volumes.push(Math.round(i * 10) / 10);
  }
  return volumes;
};

const generateMotorcycleEngineVolumes = () => {
  return [50, 80, 125, 150, 200, 250, 300, 350, 400, 500, 600, 650, 700, 750, 800, 900, 1000, 1100, 1200, 1300, 1400, 1500, 1600, 1700, 1800, 1900, 2000];
};

const GeorgianCustomsCalculator = () => {
  const currentYear = new Date().getFullYear();

  const [vehicleType, setVehicleType] = useState('car');
  const [carType, setCarType] = useState('petrol');
  const [registrationType, setRegistrationType] = useState('single');
  const [wheelPosition, setWheelPosition] = useState('left');
  const [carEngineVolume, setCarEngineVolume] = useState(1.0);
  const [motorcycleEngineVolume, setMotorcycleEngineVolume] = useState(500);
  const [vehicleYear, setVehicleYear] = useState(currentYear);

  const vehicleAge = currentYear - vehicleYear;
  const isElectric = vehicleType === 'car' && carType === 'electric';
  const isHybrid = vehicleType === 'car' && carType === 'hybrid';

  const result = useMemo(() => {
    try {
      const engineVolume = vehicleType === 'motorcycle' ? motorcycleEngineVolume : carEngineVolume;
      const engineVolumeUnit = vehicleType === 'motorcycle' ? 'CC' : 'L';

      return calculateGeorgianCustoms({
        vehicleType,
        engineVolume: isElectric ? 0 : engineVolume,
        engineVolumeUnit,
        vehicleAge,
        isElectric,
        isHybrid,
        registrationType,
        wheelPosition,
      });
    } catch (error) {
      console.error('Calculation error:', error);
      return {
        total: 0,
        breakdown: {
          exciseTax: 0,
          customsServiceFee: 150,
          registrationFee: 200,
          importTax: 0,
          expertAssessment: 30,
          customsDeclaration: 50,
          internalTransit: 50,
        },
        parameters: { vehicleType, exciseRate: 0 },
      };
    }
  }, [vehicleType, carEngineVolume, motorcycleEngineVolume, vehicleAge, isElectric, isHybrid, registrationType, wheelPosition]);

  const carEngineVolumes = useMemo(() => generateCarEngineVolumes(), []);
  const motorcycleEngineVolumes = useMemo(() => generateMotorcycleEngineVolumes(), []);

  const years = Array.from({ length: currentYear - 1979 }, (_, i) => currentYear - i);

  const engineVolumeDisplay =
    vehicleType === 'motorcycle'
      ? `${motorcycleEngineVolume} CC (${result.parameters.engineVolumeInLiters?.toFixed(2) || '0.00'}L)`
      : `${carEngineVolume}L`;

  return (
    <Card style={{ padding: 24, borderRadius: 8 }}>
      <Flex align="center" justify="space-between" style={{ marginBottom: 24 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Georgian Car Customs Calculator
        </Typography.Title>
        <Tag color="success">LIVE</Tag>
      </Flex>

      <Flex vertical gap={24}>
        <div>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Vehicle Type
          </Typography.Text>
          <Radio.Group
            value={vehicleType}
            onChange={(e) => {
              setVehicleType(e.target.value);
              if (e.target.value === 'motorcycle') {
                setCarType('petrol');
                setRegistrationType('single');
                setWheelPosition('left');
              }
            }}
            options={[
              { label: 'Car', value: 'car' },
              { label: 'Motorcycle', value: 'motorcycle' },
            ]}
          />
        </div>

        {vehicleType === 'car' && (
          <>
            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                Car Type
              </Typography.Text>
              <Radio.Group
                value={carType}
                onChange={(e) => setCarType(e.target.value)}
                options={[
                  { label: 'Petrol/Diesel', value: 'petrol' },
                  { label: 'Electric', value: 'electric' },
                  { label: 'Hybrid', value: 'hybrid' },
                ]}
              />
            </div>

            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                Wheel Position
              </Typography.Text>
              <Radio.Group
                value={wheelPosition}
                onChange={(e) => setWheelPosition(e.target.value)}
                options={[
                  { label: 'Left', value: 'left' },
                  { label: 'Right', value: 'right' },
                ]}
              />
            </div>

            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                Registration Type
              </Typography.Text>
              <Radio.Group
                value={registrationType}
                onChange={(e) => setRegistrationType(e.target.value)}
                options={[
                  { label: 'Single', value: 'single' },
                  { label: 'Double', value: 'double' },
                ]}
              />
            </div>
          </>
        )}

        <div>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Engine Volume {vehicleType === 'motorcycle' ? '(CC)' : '(Liters)'}
          </Typography.Text>
          <Select
            style={{ width: '100%' }}
            value={vehicleType === 'motorcycle' ? motorcycleEngineVolume : carEngineVolume}
            onChange={(value) => {
              if (vehicleType === 'motorcycle') {
                setMotorcycleEngineVolume(value);
              } else {
                setCarEngineVolume(value);
              }
            }}
            disabled={isElectric}
            options={
              vehicleType === 'motorcycle'
                ? motorcycleEngineVolumes.map((v) => ({ label: v, value: v }))
                : carEngineVolumes.map((v) => ({ label: v.toFixed(1), value: v }))
            }
          />
        </div>

        <div>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Year of Manufacture
          </Typography.Text>
          <Select
            style={{ width: '100%' }}
            value={vehicleYear}
            onChange={(value) => setVehicleYear(value)}
            options={years.map((y) => ({ label: y, value: y }))}
          />
        </div>
      </Flex>

      <Divider />

      <Flex vertical gap={16}>
        <Flex vertical align="center" style={{ marginBottom: 16 }}>
          <Typography.Text type="secondary" style={{ marginBottom: 8 }}>
            Total Registration Cost
          </Typography.Text>
          <Typography.Title level={2} style={{ margin: 0, color: 'var(--colorPrimary)' }}>
            {formatNumber(result.total)}{' '}
            <Typography.Text type="secondary" style={{ fontSize: 18 }}>
              ₾
            </Typography.Text>
          </Typography.Title>
        </Flex>

        <Card style={{ marginBottom: 16, background: 'var(--colorPrimary)', color: 'white' }}>
          <Typography.Text style={{ color: 'rgba(255,255,255,0.95)' }}>
            <strong>Vehicle Age:</strong> {vehicleAge} years | <strong>Engine:</strong>{' '}
            {engineVolumeDisplay} | <strong>Excise Rate:</strong>{' '}
            {result.parameters.exciseRate || 0}
            {vehicleType === 'car' && (
              <>
                {' | '}
                <strong>Type:</strong> {carType.charAt(0).toUpperCase() + carType.slice(1)}{' '}
                | <strong>Wheel:</strong> {wheelPosition.charAt(0).toUpperCase() + wheelPosition.slice(1)}{' '}
                | <strong>Registration:</strong>{' '}
                {registrationType.charAt(0).toUpperCase() + registrationType.slice(1)}
              </>
            )}
          </Typography.Text>
        </Card>

        <Card>
          {[
            ['Excise Tax', result.breakdown.exciseTax],
            ['Customs Service Tax', result.breakdown.customsServiceFee],
            ['Registration', result.breakdown.registrationFee],
            ['Import Tax', result.breakdown.importTax],
            ['Expert Assessment', result.breakdown.expertAssessment],
            ['Customs Declaration', result.breakdown.customsDeclaration],
            ['Internal Transit (up to 60 days)', result.breakdown.internalTransit],
          ].map(([label, value], i) => (
            <Flex key={label} justify="space-between" style={{ padding: '12px 0', borderBottom: i < 6 ? '1px solid var(--colorBorder)' : 'none' }}>
              <Typography.Text>{label}</Typography.Text>
              <Typography.Text strong>{formatNumber(value)} ₾</Typography.Text>
            </Flex>
          ))}
        </Card>
      </Flex>
    </Card>
  );
};

export default GeorgianCustomsCalculator;
